import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Skeleton,
} from '@tinct/web-components';
import { ReadSsoConnection, SsoProtocol } from '@tinct/core-api-models';
import { SwitchSsoProtocolModal } from '../modals/switch-sso-protocol-modal';
import { SsoOidcConnectionForm } from './sso-oidc-connection-form';
import { SsoSamlConnectionForm } from './sso-saml-connection-form';

const PROTOCOLS: { value: SsoProtocol; label: string; hint: string }[] = [
  {
    value: 'oidc',
    label: 'OpenID Connect',
    hint: 'Okta, Microsoft Entra ID, Google Workspace and most modern providers.',
  },
  {
    value: 'saml',
    label: 'SAML 2.0',
    hint: 'The enterprise standard, and the only way in with some providers.',
  },
];

export type SsoIdentityProviderCardProps = {
  organizationId: string;
  connection: ReadSsoConnection | undefined;
  isLoading: boolean;
};

/**
 * Where the admin describes their identity provider. The protocol decides which fields are asked
 * for, so each one has its own form: changing it on an existing connection is a change of trust, and
 * is confirmed before the form is swapped.
 */
export function SsoIdentityProviderCard({
  organizationId,
  connection,
  isLoading,
}: SsoIdentityProviderCardProps) {
  // The protocol the admin is filling in, which is the connection's until they pick the other one.
  const [chosenProtocol, setChosenProtocol] = useState<SsoProtocol | null>(
    null,
  );
  const [protocolToConfirm, setProtocolToConfirm] =
    useState<SsoProtocol | null>(null);
  const protocol = chosenProtocol ?? connection?.protocol ?? 'oidc';

  const handleProtocolChange = (value: string) => {
    const next = value as SsoProtocol;
    if (next === protocol) return;
    // Nothing to lose while no connection exists: swap the form straight away.
    if (!connection?.id) {
      setChosenProtocol(next);
      return;
    }
    setProtocolToConfirm(next);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Identity provider</CardTitle>
        <CardDescription>
          The provider that authenticates your team. Register Tinct as an
          application there, then paste what it gives you here.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        {isLoading ? (
          <Skeleton className="h-64 w-full rounded-xl" />
        ) : (
          <>
            <div className="flex flex-col gap-3">
              <Label>Protocol</Label>
              <RadioGroup
                value={protocol}
                onValueChange={handleProtocolChange}
                className="gap-3"
              >
                {PROTOCOLS.map((option) => (
                  <div key={option.value} className="flex items-start gap-2">
                    <RadioGroupItem
                      value={option.value}
                      id={`sso-protocol-${option.value}`}
                      aria-label={option.label}
                      className="mt-1"
                    />
                    <div className="flex flex-col">
                      <Label
                        htmlFor={`sso-protocol-${option.value}`}
                        className="font-normal"
                      >
                        {option.label}
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        {option.hint}
                      </span>
                    </div>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {protocol === 'oidc' ? (
              <SsoOidcConnectionForm
                organizationId={organizationId}
                connection={connection}
              />
            ) : (
              <SsoSamlConnectionForm
                organizationId={organizationId}
                connection={connection}
              />
            )}
          </>
        )}
      </CardContent>

      {protocolToConfirm && (
        <SwitchSsoProtocolModal
          open
          onOpenChange={(open) => !open && setProtocolToConfirm(null)}
          protocol={protocolToConfirm}
          onConfirm={() => {
            setChosenProtocol(protocolToConfirm);
            setProtocolToConfirm(null);
          }}
        />
      )}
    </Card>
  );
}
